// Iter101 γ-3-I — Platform-admin AI usage observability.
//
// Aggregated view of every AI intake call: total, by user, by tenant,
// by context, by day. Matches the pattern of the existing user-activity
// analytics page — same StatCard idiom, same 7/30/90 window selector.
// NOT a quota enforcer — just observability for pilot pricing decisions.
import React, { useState } from "react";
import { Sparkles, RefreshCw, BarChart3, Users, Building2, Layers, Calendar } from "lucide-react";
import { useAiUsageSummary } from "../api/aiIntake";

const WINDOWS = [
  { d: 7, label: "Last 7d" },
  { d: 30, label: "Last 30d" },
  { d: 90, label: "Last 90d" },
];

const Card = ({ icon: Icon, label, value, sub, testid, accent = "#185FA5" }) => (
  <div
    className="rounded p-4"
    style={{ background: "var(--bg-surface, #ffffff)", border: "1px solid var(--border-strong, rgba(0,0,0,0.12))", borderLeft: `3px solid ${accent}` }}
    data-testid={testid}
  >
    <div className="flex items-center gap-2 mb-2">
      <Icon size={14} style={{ color: accent }} />
      <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "var(--text-dimmer)", letterSpacing: "0.5px", textTransform: "uppercase" }}>
        {label}
      </span>
    </div>
    <div style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 28, color: "var(--text-primary)" }}>
      {value}
    </div>
    {sub && (
      <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "var(--text-dimmer)", marginTop: 4 }}>
        {sub}
      </div>
    )}
  </div>
);

const SimpleTable = ({ rows, columns, testid, emptyLabel = "No data yet" }) => (
  <div
    className="rounded overflow-hidden"
    style={{ background: "var(--bg-surface, #ffffff)", border: "1px solid var(--border-strong, rgba(0,0,0,0.12))" }}
    data-testid={testid}
  >
    <table style={{ width: "100%", borderCollapse: "collapse" }}>
      <thead>
        <tr style={{ background: "var(--bg, #f6f7fb)" }}>
          {columns.map((c) => (
            <th
              key={c.key}
              style={{
                padding: "8px 12px", textAlign: c.align || "left",
                fontFamily: "'DM Mono', monospace", fontSize: 10, color: "var(--text-dimmer)",
                letterSpacing: "0.5px", textTransform: "uppercase", borderBottom: "1px solid var(--border-strong, rgba(0,0,0,0.10))",
              }}
            >
              {c.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 && (
          <tr>
            <td colSpan={columns.length} style={{ padding: "20px", textAlign: "center", fontFamily: "'DM Mono', monospace", fontSize: 11, color: "var(--text-dimmer)" }}>
              {emptyLabel}
            </td>
          </tr>
        )}
        {rows.map((r, i) => (
          <tr key={i} style={{ borderTop: "1px solid var(--border-soft, rgba(0,0,0,0.05))" }}>
            {columns.map((c) => (
              <td
                key={c.key}
                style={{ padding: "8px 12px", textAlign: c.align || "left", fontFamily: "'DM Mono', monospace", fontSize: 11, color: "var(--text-primary)" }}
              >
                {c.render ? c.render(r) : r[c.key] ?? "—"}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// Tiny inline sparkline for the by-day trend.
const Sparkline = ({ points, height = 40, color = "#185FA5" }) => {
  if (!points || points.length === 0) {
    return <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "var(--text-dimmer)" }}>No daily data</div>;
  }
  const max = Math.max(...points.map((p) => p.value), 1);
  const w = Math.max(points.length * 12, 240);
  const stepX = (w - 8) / Math.max(points.length - 1, 1);
  const pathD = points
    .map((p, i) => `${i === 0 ? "M" : "L"} ${4 + i * stepX} ${height - 4 - (p.value / max) * (height - 8)}`)
    .join(" ");
  return (
    <svg width="100%" height={height} viewBox={`0 0 ${w} ${height}`} preserveAspectRatio="none" data-testid="admin-ai-usage-sparkline">
      <path d={pathD} fill="none" stroke={color} strokeWidth="1.6" />
      {points.map((p, i) => (
        <circle key={i} cx={4 + i * stepX} cy={height - 4 - (p.value / max) * (height - 8)} r="2" fill={color}>
          <title>{p.day}: {p.value} calls</title>
        </circle>
      ))}
    </svg>
  );
};

export default function AdminAiUsage() {
  const [days, setDays] = useState(30);
  const q = useAiUsageSummary(days);
  const d = q.data || {};
  const totals = d.totals || {};
  const successRate = totals.calls > 0 ? Math.round((totals.success / totals.calls) * 100) : 100;
  const byDayPoints = (d.by_day || []).map((r) => ({ day: r.day, value: r.calls }));

  return (
    <div className="max-w-6xl mx-auto px-4 py-6" data-testid="admin-ai-usage-page">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h1 style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 22, display: "flex", alignItems: "center", gap: 8 }}>
            <Sparkles size={20} style={{ color: "#7e3aa8" }} /> AI usage · pilot observability
          </h1>
          <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "var(--text-dimmer)", marginTop: 2, letterSpacing: "0.3px" }}>
            Total calls · per user · per tenant · per context · per day. No usage limits — observability only.
          </div>
        </div>
        <div className="flex items-center gap-2">
          {WINDOWS.map((w) => (
            <button
              key={w.d}
              type="button"
              onClick={() => setDays(w.d)}
              className="px-2.5 py-1 rounded"
              style={{
                background: days === w.d ? "#7e3aa8" : "transparent",
                color: days === w.d ? "#ffffff" : "var(--text-dimmer)",
                border: `1px solid ${days === w.d ? "#4f1e6a" : "rgba(0,0,0,0.15)"}`,
                fontFamily: "'DM Mono', monospace", fontSize: 11, fontWeight: 700, cursor: "pointer", letterSpacing: "0.3px",
              }}
              data-testid={`admin-ai-usage-window-${w.d}`}
            >
              {w.label}
            </button>
          ))}
          <button
            type="button"
            onClick={() => q.refetch()}
            disabled={q.isFetching}
            className="px-2.5 py-1 rounded inline-flex items-center gap-1"
            style={{ background: "transparent", border: "1px solid rgba(0,0,0,0.15)", fontFamily: "'DM Mono', monospace", fontSize: 11, cursor: "pointer" }}
            data-testid="admin-ai-usage-refresh"
          >
            <RefreshCw size={11} className={q.isFetching ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <Card icon={BarChart3} label="Total calls" value={totals.calls ?? 0} sub={`${successRate}% success`} testid="admin-ai-usage-total" />
        <Card icon={Sparkles} label="Est. tokens" value={(totals.est_tokens ?? 0).toLocaleString()} sub={`Input ${(totals.input_chars ?? 0).toLocaleString()} chars · Output ${(totals.output_chars ?? 0).toLocaleString()} chars`} testid="admin-ai-usage-tokens" accent="#7e3aa8" />
        <Card icon={Users} label="Distinct users" value={(d.by_user || []).length} sub="See breakdown below" testid="admin-ai-usage-users" accent="#22a35a" />
        <Card icon={Building2} label="Distinct tenants" value={(d.by_tenant || []).length} sub="See breakdown below" testid="admin-ai-usage-tenants" accent="#185FA5" />
      </div>

      <div className="rounded p-4 mb-4" style={{ background: "var(--bg-surface, #ffffff)", border: "1px solid var(--border-strong, rgba(0,0,0,0.12))" }} data-testid="admin-ai-usage-trend">
        <div className="flex items-center gap-2 mb-2">
          <Calendar size={13} style={{ color: "#7e3aa8" }} />
          <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "var(--text-dimmer)", letterSpacing: "0.5px", textTransform: "uppercase" }}>
            Calls per day · {days}d window
          </span>
        </div>
        <Sparkline points={byDayPoints} color="#7e3aa8" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-4">
        <div>
          <div style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 13, marginBottom: 6, color: "var(--text-primary)" }}>
            By user (top 50)
          </div>
          <SimpleTable
            rows={d.by_user || []}
            columns={[
              { key: "user_name", label: "Name", render: (r) => r.user_name || r.user_email || r.user_id || "—" },
              { key: "user_email", label: "Email" },
              { key: "calls", label: "Calls", align: "right", render: (r) => <strong>{r.calls}</strong> },
              { key: "est_tokens", label: "Est. tokens", align: "right", render: (r) => r.est_tokens.toLocaleString() },
            ]}
            testid="admin-ai-usage-by-user"
          />
        </div>
        <div>
          <div style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 13, marginBottom: 6, color: "var(--text-primary)" }}>
            By tenant
          </div>
          <SimpleTable
            rows={d.by_tenant || []}
            columns={[
              { key: "tenant_id", label: "Tenant", render: (r) => r.tenant_id || "(no tenant)" },
              { key: "calls", label: "Calls", align: "right", render: (r) => <strong>{r.calls}</strong> },
              { key: "est_tokens", label: "Est. tokens", align: "right", render: (r) => r.est_tokens.toLocaleString() },
            ]}
            testid="admin-ai-usage-by-tenant"
          />
        </div>
      </div>

      <div>
        <div className="flex items-center gap-2 mb-2">
          <Layers size={13} style={{ color: "var(--text-dimmer)" }} />
          <span style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 13, color: "var(--text-primary)" }}>
            By context
          </span>
        </div>
        <SimpleTable
          rows={d.by_context || []}
          columns={[
            { key: "context", label: "Context", render: (r) => <code style={{ fontFamily: "'DM Mono', monospace", fontSize: 11 }}>{r.context || "(unset)"}</code> },
            { key: "calls", label: "Calls", align: "right", render: (r) => <strong>{r.calls}</strong> },
            { key: "est_tokens", label: "Est. tokens", align: "right", render: (r) => r.est_tokens.toLocaleString() },
          ]}
          testid="admin-ai-usage-by-context"
        />
      </div>
    </div>
  );
}
